import React, { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSearchButton from "src/components/core-components/mui/buttons/ByzMuiSearchButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import SelectedServicesPanel from "src/components/shared-components/selected-services-panel";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import EditOperationModal from "./partials/EditOperationModal";
import WillOpenCardModals from "./partials/WillOpenCardModals";
import { useWillOpenCardState } from "./hooks/useWillOpenCardLogic";
import { useServiceSelectionState } from "./hooks/useServiceSelectionState";
import { useCategoryManagementState } from "./hooks/useCategoryManagementState";
import { useServiceValidation } from "./hooks/useServiceValidation";
import { useTranslate } from "src/locales";
import SidebarArea from "./partials/SidebarArea";
import ServiceGridArea from "./partials/ServiceGridArea";
import {
  requestService,
  FindServicePricesSimpleRequestDto,
  SelectServiceSimpleRequestDto,
  ConfirmSelectionRequestDto,
} from "src/api/request/requestService";
import { patientDataService } from "src/api/patient-datas/patientDatasService";
import { useSelector } from "react-redux";
import { RootState } from "src/app/store";

import {
  Box,
  InputAdornment,
  Checkbox,
  IconButton,
  Stack,
  FormControlLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSelect from "src/components/core-components/mui/ByzMuiSelect";
import { ByzMuiTypography } from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { nurseService } from "src/api/nurse/nurseService";
import dayjs from "dayjs";
import { resolveWillOpenCards, ResolvedWillOpenCard } from "../helpers/willOpenCardHelper";

interface RequestServiceModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  rowID: string;
  upn?: string;
}

const RequestServiceModal: React.FC<RequestServiceModalProps> = ({
  open,
  onClose,
  title,
  description,
  rowID,
  upn,
}) => {
  const { t } = useTranslate();
  const selectedOfficeId = useSelector(
    (state: RootState) => state.auth.selectedOfficeId
  );
  // Service selection state management via hook
  const {
    selectedServices, setSelectedServices,
    selectedServicesData, setSelectedServicesData,
    selectedHistoryServices, setSelectedHistoryServices,
    services, setServices,
    loadingServices, setLoadingServices,
    isServiceProcessing, setIsServiceProcessing,
    isSubmittingToPatient, setIsSubmittingToPatient,
    editModalOpen, setEditModalOpen,
    editRow, setEditRow,
    huv, setHuv,
    pendingServiceQueue, setPendingServiceQueue,
  } = useServiceSelectionState();

  // WillOpenCard state management via hook
  const {
    pendingWillOpenCards, setPendingWillOpenCards,
    activeWillOpenCardIndex, setActiveWillOpenCardIndex,
    pendingConfirmSelection, setPendingConfirmSelection,
    willOpenCardFlowValid, setWillOpenCardFlowValid,
    serviceAddInfo, setServiceAddInfo,
    activeWillOpenCardServiceName, setActiveWillOpenCardServiceName,
    openHeightInfoModal, setOpenHeightInfoModal,
    openVkiModal, setOpenVkiModal,
    openEgfrModal, setOpenEgfrModal,
    openDirectionModal, setOpenDirectionModal,
    openAskDoctorModal, setOpenAskDoctorModal,
    openPregnantModal, setOpenPregnantModal,
    openPregnantControlModal, setOpenPregnantControlModal,
    openProcessDirectionModal, setOpenProcessDirectionModal,
    openHivModal, setOpenHivModal,
    openPacemakerModal, setOpenPacemakerModal,
    openLabModal, setOpenLabModal,
    openInvestigationModal, setOpenInvestigationModal,
    openContrastModal, setOpenContrastModal,
    openContrastRequestModal, setOpenContrastRequestModal,
    openConsultationModal, setOpenConsultationModal,
    openPathologyModal, setOpenPathologyModal,
    pendingAddToPatient, setPendingAddToPatient,
  } = useWillOpenCardState();

  const [priceListPeriods, setPriceListPeriods] = useState<any[]>([]);
  const [selectedPlpId, setSelectedPlpId] = useState<number | null>(null);

  const {
    selectedMainCategory, setSelectedMainCategory,
    selectedSubCategory, setSelectedSubCategory,
    serviceCategories, setServiceCategories,
    expandedCategories, setExpandedCategories,
    currentView, setCurrentView,
    sidebarSearchText, setSidebarSearchText,
    contentSearchText, setContentSearchText,
    visibleCategories,
    mainCategories,
    handleMainCategorySelect,
    handleSubCategorySelect,
    handleCategoryToggle,
  } = useCategoryManagementState({
    huv,
    setServices,
    setLoadingServices,
    open,
    plpId: selectedPlpId
  });
  // Patient-specific states (not managed by hook)
  const [patientAge, setPatientAge] = useState<number | null>(null);
  const [patientHeight, setPatientHeight] = useState<string>("");
  const [patientGender, setPatientGender] = useState<string | null>(null);
  const [protocolId, setProtocolId] = useState<number | null>(null);

  const {
    upsertAddInfoForPendingService,
    handleVkiModalSave,
    handleEgfrModalSave,
    handleDirectionSave,
    handleAskDoctorSave,
    handlePregnantSave,
    handleHivSave,
    handlePacemakerSave,
    handleLabSave,
    handleInvestigationSave,
    handleAddToPatientClick,
    setDoctorModalData,
  } = useServiceValidation({
    selectedServicesData,
    setSelectedServicesData,
    setSelectedServices,
    patientAge,
    patientHeight,
    patientGender,
    rowID,

    setOpenVkiModal,
    setOpenEgfrModal,
    setOpenDirectionModal,
    setOpenAskDoctorModal,
    setOpenPregnantModal,
    setOpenHivModal,
    setOpenPacemakerModal,
    setOpenLabModal,
    setOpenInvestigationModal,

    pendingAddToPatient,
    setPendingAddToPatient,
    isSubmittingToPatient,
    setIsSubmittingToPatient,

    serviceAddInfo,
    setServiceAddInfo,
    pendingConfirmSelection,

    onClose,
  });

  useEffect(() => {
    if (!open) return;
    setSelectedServices([]);
    setSelectedServicesData([]);
    setServiceAddInfo({});
    setDoctorModalData(null);
    setSelectedMainCategory("tümü");
    setSelectedSubCategory("");
    setCurrentView("main");

    const fetchPatientInfo = async () => {
      if (!rowID) return;
      try {
        const patient: any = await nurseService.patient.getPatientInfo({ id: Number(rowID) });
        const data: any = patient?.data || patient;
        const birth =
          data?.birthdate ||
          data?.birthDate ||
          data?.BIRTH_DATE ||
          data?.BirthDate ||
          data?.dob;
        if (birth) {
          const age = dayjs().diff(dayjs(birth), "year");
          setPatientAge(age);
        }
        if (data?.height) {
          setPatientHeight(String(data.height));
        }
        if (data?.gender || data?.GENDER || data?.sex || data?.Sex) {
          const g = data.gender ?? data.GENDER ?? data.sex ?? data.Sex;
          setPatientGender(String(g));
        }
      } catch (err) {
        console.error("Hasta bilgisi alınamadı:", err);
      }
    };
    fetchPatientInfo();
  }, [open, rowID]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [serviceCategoriesResponse, patientInfoResponse, plpResponse] = await Promise.all([
          requestService.getServiceCategoriesWithNames(),
          patientDataService.all(Number(rowID)),
          requestService.getPriceListPeriods()
        ]);

        if (serviceCategoriesResponse && (serviceCategoriesResponse.data || Array.isArray(serviceCategoriesResponse))) {
          setServiceCategories(serviceCategoriesResponse.data || serviceCategoriesResponse);
        }

        if (plpResponse && plpResponse.data) {
          setPriceListPeriods(plpResponse.data);
        }

        if (patientInfoResponse && patientInfoResponse.protid) {
          setProtocolId(patientInfoResponse.protid);
        } else if (patientInfoResponse && patientInfoResponse.data && patientInfoResponse.data.protid) {
          setProtocolId(patientInfoResponse.data.protid);
        }

      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    if (open) {
      fetchInitialData();
    }
  }, [open, rowID]);

  const getServiceData = React.useCallback((id: number | string, explicitService?: any) => {
    const service = explicitService || services.find((s) => s.id === id);

    const serviceData: any = {
      ...service,
      id,
      control_type: service?.pct_name || service?.control_type || service?.hiz_ist_kont_turu || null,
      name: service?.name || service?.process_name || "",
      note: "",
      quantity: 1,
      process_id: id,
      or_id: service?.or_id || 0,
      code: service?.code || "",
      st_name: service?.st_name || "",
      rt_name: service?.rt_name || "",
      gpc_name: service?.gpc_name || "",
      sc_name: service?.sc_name || "",
    };

    return serviceData;
  }, [services]);

  const fetchPriceForService = React.useCallback(async (serviceId: number, quantity: number = 1) => {
    try {
      if (!rowID) return;
      const req: FindServicePricesSimpleRequestDto = {
        ptid: parseInt(rowID as any, 10),
        serviceid: serviceId,
        quantity: quantity,
      };
      const resp = await requestService.findServicePricesSimple(req);
      const detail = resp?.data?.servicedetail;
      const priceLines = resp?.data?.pricelines || [];
      if (detail) {
        const breakdown = Array.isArray(priceLines)
          ? priceLines.map((pl: any) => ({
            kurumAdi: pl.associationid === -1 ? "Hasta Hizmet Farkı" : `Kurum : ${pl.associationname || ""}`,
            fiyat: pl.price,
            indirimOrani: 0,
            toplamTutar: pl.totalprice,
            indirimTutari: 0,
            kdv: pl.taxtotal,
            tevkifatTutar: 0,
            tevOrani: 0,
            netTutar: pl.nettotal,
          }))
          : [];

        setSelectedServicesData((prev) => prev.map((it) =>
          Number(it.id) === serviceId
            ? {
              ...it,
              patientTotal: detail.pattotalamount,
              institutionTotal: detail.antotalamount,
              total: detail.total,
              breakdown,
            }
            : it
        ));
      }
    } catch (error) {
      console.error("FindServicePricesSimple error:", error);
    }
  }, [rowID]);

  useEffect(() => {
    let newIds: (number | string)[] = [];
    setSelectedServicesData((prevData) => {
      const preservedData = prevData.filter((item) => selectedServices.includes(item.id));
      const existingIds = new Set(prevData.map((item) => item.id));
      newIds = selectedServices.filter((id) => !existingIds.has(id));
      const newData = newIds.map((id) => getServiceData(id));
      const allSelectedIds = new Set(selectedServices);
      return [...preservedData, ...newData].filter((item) => allSelectedIds.has(item.id));
    });
    (async () => {
      try {
        if (!rowID) return;
        const idsToFetch = (newIds || []).map((id) => Number(id)).filter((n) => !Number.isNaN(n));
        await Promise.all(
          idsToFetch.map(async (sid) => {
            await fetchPriceForService(sid);
          })
        );
      } catch { }
    })();
  }, [selectedServices, getServiceData, fetchPriceForService, rowID]);

  const closeAllWillOpenCardModals = React.useCallback(() => {
    setOpenHeightInfoModal(false);
    setOpenProcessDirectionModal(false);
    setOpenDirectionModal(false);
    setOpenAskDoctorModal(false);
    setOpenPregnantControlModal(false);
    setOpenPregnantModal(false);
    setOpenHivModal(false);
    setOpenPacemakerModal(false);
    setOpenLabModal(false);
    setOpenInvestigationModal(false);
    setOpenVkiModal(false);
    setOpenEgfrModal(false);
    setOpenContrastModal(false);
    setOpenContrastRequestModal(false);
    setOpenConsultationModal(false);
    setOpenPathologyModal(false);
  }, []);

  const openModalForCardRef = React.useRef<(card?: ResolvedWillOpenCard, explicitPendingConfirm?: ConfirmSelectionRequestDto & { serviceId: number | string }, explicitCurrentIndex?: number) => void>(() => { });

  const handleWillOpenCardModalClosed = React.useCallback(async (explicitCurrentIndex?: number) => {
    const currentIndex = explicitCurrentIndex ?? activeWillOpenCardIndex;
    const nextIndex = currentIndex !== null ? currentIndex + 1 : null;

    if (
      nextIndex !== null &&
      pendingWillOpenCards.length &&
      nextIndex < pendingWillOpenCards.length
    ) {
      setActiveWillOpenCardIndex(nextIndex);
      openModalForCardRef.current(pendingWillOpenCards[nextIndex], undefined, nextIndex);
    } else {
      setActiveWillOpenCardIndex(null);
      setPendingWillOpenCards([]);
      closeAllWillOpenCardModals();

      if (pendingConfirmSelection && willOpenCardFlowValid) {
        try {
          const confirmRequest: ConfirmSelectionRequestDto = {
            ptId: pendingConfirmSelection.ptId,
            processId: pendingConfirmSelection.processId,
            isSelected: pendingConfirmSelection.isSelected,
            quantity: pendingConfirmSelection.quantity,
            officeId: pendingConfirmSelection.officeId,
          };

          await requestService.confirmSelection(confirmRequest);
        } catch (confirmError) {
          console.error("Confirm Selection Error:", confirmError);
          enqueueSnackbar("Hizmet seçimi onaylanırken hata oluştu", {
            variant: "error",
          });
        } finally {
          const serviceIdToAdd = pendingConfirmSelection.serviceId;
          const numericId = Number(serviceIdToAdd);

          setSelectedServices((prev) =>
            prev.includes(serviceIdToAdd) ? prev : [...prev, serviceIdToAdd]
          );

          if (!Number.isNaN(numericId)) {
            setSelectedServicesData((prevData) =>
              prevData.some((d) => Number(d.id) === numericId)
                ? prevData
                : [...prevData, getServiceData(numericId)]
            );
            fetchPriceForService(numericId);
          }

          setPendingConfirmSelection(null);
        }
      } else if (pendingAddToPatient && willOpenCardFlowValid) {
        handleAddToPatientClick();
      } else {
        setPendingConfirmSelection(null);
        setPendingAddToPatient(false);
      }
    }
  }, [activeWillOpenCardIndex, pendingWillOpenCards, pendingConfirmSelection, willOpenCardFlowValid, pendingAddToPatient, closeAllWillOpenCardModals, getServiceData, fetchPriceForService, handleAddToPatientClick, setPendingAddToPatient]);

  const openModalForCard = React.useCallback((card?: ResolvedWillOpenCard, explicitPendingConfirm?: ConfirmSelectionRequestDto & { serviceId: number | string }, explicitCurrentIndex?: number) => {
    if (!card) {
      handleWillOpenCardModalClosed();
      return;
    }

    let dataKeyToFind: string | null = null;

    switch (card.key) {
      case "HEIGHT_INFO":
      case "HEIGHT_INFO_REQUEST":
        dataKeyToFind = "HEIGHT_INFO";
        break;
      case "EGFR":
      case "EGFR_REQUEST":
        dataKeyToFind = "EGFR";
        break;
      case "PREGNANT_CONTROL":
      case "PREGNANT_CONTROL_REQUEST":
        dataKeyToFind = "PREGNANT_CONTROL";
        break;
      case "PACEMAKER":
      case "PACEMAKER_CONTROL":
      case "PACEMAKER_CONTROL_REQUEST":
        dataKeyToFind = "PACEMAKER_CONTROL";
        break;
    }

    if (dataKeyToFind) {
      const searchKey = dataKeyToFind;

      const existingDataEntry = Object.values(serviceAddInfo).find(
        (info: any) => info && info[searchKey]
      );

      if (existingDataEntry && (existingDataEntry as any)[searchKey]) {
        const targetServiceId = explicitPendingConfirm?.serviceId ?? pendingConfirmSelection?.serviceId;
        if (targetServiceId) {
          upsertAddInfoForPendingService(searchKey, (existingDataEntry as any)[searchKey], targetServiceId);
        }
        handleWillOpenCardModalClosed(explicitCurrentIndex);
        return;
      }
    }

    closeAllWillOpenCardModals();

    setTimeout(() => {
      switch (card.key) {
        case "HEIGHT_INFO":
        case "HEIGHT_INFO_REQUEST":
          setOpenHeightInfoModal(true);
          return;
        case "PROCESS_DIRECTION":
        case "PROCESS_DIRECTION_REQUEST":
          setOpenProcessDirectionModal(true);
          return;
        case "ASK_DOCTOR":
        case "ASK_DOCTOR_REQUEST":
          setOpenAskDoctorModal(true);
          return;
        case "PREGNANT_CONTROL":
        case "PREGNANT_CONTROL_REQUEST":
          setOpenPregnantControlModal(true);
          return;
        case "HIV":
        case "HIV_REQUEST":
          setOpenHivModal(true);
          return;
        case "PACEMAKER":
        case "PACEMAKER_CONTROL":
        case "PACEMAKER_CONTROL_REQUEST":
          setOpenPacemakerModal(true);
          return;
        case "CONSULTATION":
        case "CONSULTATION_REQUEST":
          setOpenConsultationModal(true);
          return;
        case "LAB_REQUEST":
          setOpenLabModal(true);
          return;
        case "PATHOLOGY":
        case "PATHOLOGY_REQUEST":
          setOpenPathologyModal(true);
          return;
        case "PATIENT_FILE_CHANGE":
          // setOpenPatientFileChangeModal(true);
          return;
        case "CONTRAST":
          setOpenContrastModal(true);
          return;
        case "CONTRAST_REQUEST":
          setOpenContrastRequestModal(true);
          return;
        case "EXAMINATION_REQUEST":
          setOpenInvestigationModal(true);
          return;
        case "EGFR":
        case "EGFR_REQUEST":
          setOpenEgfrModal(true);
          return;
        default:
          handleWillOpenCardModalClosed();
      }
    }, 10);
  }, [
    handleWillOpenCardModalClosed,
    serviceAddInfo,
    pendingConfirmSelection,
    upsertAddInfoForPendingService,
    closeAllWillOpenCardModals,
    setOpenHeightInfoModal,
    setOpenProcessDirectionModal,
    setOpenAskDoctorModal,
    setOpenPregnantControlModal,
    setOpenHivModal,
    setOpenPacemakerModal,
    setOpenConsultationModal,
    setOpenLabModal,
    setOpenPathologyModal,
    setOpenContrastModal,
    setOpenContrastRequestModal,
    setOpenInvestigationModal,
    setOpenEgfrModal,
  ]);

  React.useEffect(() => {
    openModalForCardRef.current = openModalForCard;
  }, [openModalForCard]);

  const handleWillOpenCardModalCancelled = () => {
    setWillOpenCardFlowValid(false);

    // Hizmet seçimini geri al
    if (pendingConfirmSelection?.serviceId !== undefined) {
      const serviceIdToRevert = pendingConfirmSelection.serviceId;
      setSelectedServices((prev) =>
        prev.includes(serviceIdToRevert)
          ? prev.filter((id) => id !== serviceIdToRevert)
          : prev
      );
      setSelectedServicesData((prev) =>
        prev.filter((item) => item.id !== serviceIdToRevert)
      );
      if (pendingConfirmSelection.historyGridId) {
        const hGridId = pendingConfirmSelection.historyGridId;
        setSelectedHistoryServices((prev) =>
          prev.filter((id) => id !== hGridId)
        );
      } else {
        setSelectedHistoryServices((prev) =>
          prev.includes(serviceIdToRevert)
            ? prev.filter((id) => id !== serviceIdToRevert)
            : prev
        );
      }

      setServiceAddInfo((prev) => {
        const copy = { ...prev };
        delete copy[String(serviceIdToRevert)];
        return copy;
      });
    }

    setActiveWillOpenCardIndex(null);
    setPendingWillOpenCards([]);
    setPendingConfirmSelection(null);
    closeAllWillOpenCardModals();
  };

  const handleBackendSelectService = React.useCallback(
    async (serviceId: number | string, explicitService?: any, associatedHistoryId?: number | string) => {
      try {
        const service = explicitService ||
          services.find((s) => String(s.id) === String(serviceId));
        const processId = service?.process_id || service?.id;

        const requestData: SelectServiceSimpleRequestDto = {
          ptid: Number(rowID),
          processid: processId || 0,
          officeid: selectedOfficeId || 0,
          isselected: true,
          quantity: 1,
          note: "",
        };

        const response = await requestService.selectServiceSimple(requestData);

        if (!response.issuccessful) {
          enqueueSnackbar(response.errormessage || "Hizmet seçilemedi", {
            variant: "error",
          });
          return;
        }

        const rawWillOpenCard = (response as any).willopencard ?? response.validationresult?.willopencard;

        const incomingCards = resolveWillOpenCards(rawWillOpenCard);

        if (incomingCards.length) {
          setWillOpenCardFlowValid(true);
          const serviceNameForCards =
            (service as any)?.name ||
            (service as any)?.NAME ||
            undefined;
          setActiveWillOpenCardServiceName(serviceNameForCards);
          setPendingWillOpenCards(incomingCards);
          setActiveWillOpenCardIndex(0);
          const pendingConfirm = {
            ptId: Number(rowID),
            processId: processId || 0,
            isSelected: true,
            quantity: 1,
            officeId: selectedOfficeId || undefined,
            serviceId,
            historyGridId: associatedHistoryId,
          };
          setPendingConfirmSelection(pendingConfirm);
          openModalForCard(incomingCards[0], pendingConfirm, 0);
          return;
        }

      } catch {
        // swallow select-service-simple error in add-service flow
      }
    },
    [rowID, selectedOfficeId, services, openModalForCard]
  );

  const handleServiceToggle = React.useCallback(
    async (serviceId: number | string, explicitService?: any, associatedHistoryId?: number | string) => {
      if (isServiceProcessing) return;

      setIsServiceProcessing(true);
      try {
        const realServiceId = explicitService?.process_id || explicitService?.id || serviceId;
        const isAdding = !selectedServices.includes(realServiceId);

        if (isAdding) {
          await handleBackendSelectService(realServiceId, explicitService, associatedHistoryId);
        } else if (pendingConfirmSelection?.serviceId === Number(realServiceId)) {
          setPendingConfirmSelection(null);
          setPendingWillOpenCards([]);
          closeAllWillOpenCardModals();
        }

        setSelectedServices((prev) => {
          const isAddingNow = !prev.includes(realServiceId);
          return isAddingNow
            ? [...prev, realServiceId]
            : prev.filter((id) => id !== realServiceId);
        });

        if (associatedHistoryId !== undefined) {
          setSelectedHistoryServices((prev) =>
            prev.includes(associatedHistoryId)
              ? prev.filter((id) => id !== associatedHistoryId)
              : [...prev, associatedHistoryId]
          );
        }

        if (isAdding) {
          const numericId = Number(realServiceId);
          if (!Number.isNaN(numericId)) {
            setSelectedServicesData((prevData) =>
              prevData.some((d) => Number(d.id) === numericId)
                ? prevData
                : [...prevData, getServiceData(numericId, explicitService)]
            );
            fetchPriceForService(numericId);
          }
        } else {
          setSelectedServicesData((prev) =>
            prev.filter((item) => String(item.id) !== String(realServiceId))
          );
          setServiceAddInfo((prev) => {
            const copy = { ...prev };
            delete copy[String(realServiceId)];
            return copy;
          });
        }

      } catch (error) {
        console.error("Service toggle error", error);
      } finally {
        setIsServiceProcessing(false);
      }
    },
    [isServiceProcessing, selectedServices, handleBackendSelectService, getServiceData, fetchPriceForService, pendingConfirmSelection, closeAllWillOpenCardModals, setSelectedHistoryServices]
  );



  const handleHistoryParentToggle = (parentId: number | string, children?: any[]) => {
    if (!children) return;

    const childIds = children.map(child => child.id);
    const allChildrenSelected = childIds.every((id: any) => selectedHistoryServices.includes(id));

    if (allChildrenSelected) {
      const itemsToQueue = childIds
        .filter((id: any) => selectedHistoryServices.includes(id))
        .map((id: any) => {
          const child = children.find(c => c.id === id);
          return {
            serviceId: id,
            explicitService: child?.originalData ? {
              id: child.originalData.process_id || child.originalData.id || id,
              name: child.originalData.process_name || (child.originalData as any).name || "",
              code: (child.originalData as any).code || "",
              st_name: (child.originalData as any).st_name || "",
              rt_name: (child.originalData as any).rt_name || "",
              sc_name: (child.originalData as any).sc_name || "",
              process_id: child.originalData.process_id || child.originalData.id || id,
            } : undefined,
            associatedHistoryId: id
          };
        });
      setPendingServiceQueue(prev => [...prev, ...itemsToQueue]);
    } else {
      const itemsToQueue = childIds
        .filter((id: any) => !selectedHistoryServices.includes(id))
        .map((id: any) => {
          const child = children.find(c => c.id === id);
          return {
            serviceId: id,
            explicitService: child?.originalData ? {
              id: child.originalData.process_id || child.originalData.id || id,
              name: child.originalData.process_name || (child.originalData as any).name || "",
              code: (child.originalData as any).code || "",
              st_name: (child.originalData as any).st_name || "",
              rt_name: (child.originalData as any).rt_name || "",
              sc_name: (child.originalData as any).sc_name || "",
              process_id: child.originalData.process_id || child.originalData.id || id,
            } : undefined,
            associatedHistoryId: id
          };
        });
      setPendingServiceQueue(prev => [...prev, ...itemsToQueue]);
    }
  };

  useEffect(() => {
    if (
      pendingServiceQueue.length > 0 &&
      !isServiceProcessing &&
      !pendingConfirmSelection &&
      pendingWillOpenCards.length === 0
    ) {
      const nextItem = pendingServiceQueue[0];
      setPendingServiceQueue((prev) => prev.slice(1));

      const realServiceId = nextItem.explicitService?.id || nextItem.serviceId;
      handleServiceToggle(realServiceId, nextItem.explicitService, nextItem.associatedHistoryId);
      // selectedHistoryServices güncellemesi artık handleServiceToggle içinde yapılıyor
    }
  }, [pendingServiceQueue, isServiceProcessing, pendingConfirmSelection, pendingWillOpenCards, handleServiceToggle]);



  const gridData = React.useMemo(() => {
    return services;
  }, [services]);

  const handleAddToFavorites = React.useCallback(async (serviceId: number, serviceName?: string) => {
    try {
      setLoadingServices(true);
      const result = await requestService.addServiceFavorite(serviceId);
      const success = result?.issuccess ?? result?.data;

      if (success && currentView === "frequent") {
        const favs = await requestService.getAddServiceFavorites(huv);
        if (Array.isArray(favs?.data)) setServices(favs.data);
      }

      if (success) {
        enqueueSnackbar(
          serviceName
            ? t("patient-request-service.notifications.favorites.add-success", "default", { service: serviceName })
            : t("patient-request-service.notifications.favorites.add-success-generic"),
          { variant: "success", autoHideDuration: 2500 }
        );
      } else {
        enqueueSnackbar(t("patient-request-service.notifications.favorites.add-warning"), {
          variant: "warning",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error adding to favorites:", error);
      enqueueSnackbar(t("patient-request-service.notifications.favorites.add-error"), {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setLoadingServices(false);
    }
  }, [currentView, huv, t]);

  const handleRemoveFromFavorites = React.useCallback(async (serviceId: number, serviceName?: string) => {
    try {
      setLoadingServices(true);
      const result = await requestService.removeServiceFavorite(serviceId);
      const success = result?.issuccess ?? result?.data;

      if (success && currentView === "frequent") {
        const favs = await requestService.getAddServiceFavorites(huv);
        if (Array.isArray(favs?.data)) setServices(favs.data);
      }

      if (success) {
        enqueueSnackbar(
          serviceName
            ? t("patient-request-service.notifications.favorites.remove-success", "default", { service: serviceName })
            : t("patient-request-service.notifications.favorites.remove-success-generic"),
          { variant: "info", autoHideDuration: 2500 }
        );
      } else {
        enqueueSnackbar(t("patient-request-service.notifications.favorites.remove-warning"), {
          variant: "warning",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error removing from favorites:", error);
      enqueueSnackbar(t("patient-request-service.notifications.favorites.remove-error"), {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setLoadingServices(false);
    }
  }, [currentView, huv, t]);

  const columns: GridColDef[] = React.useMemo(() => {
    const base: GridColDef[] = [
      {
        field: "select",
        headerName: "",
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={selectedServices.includes(params.row.id)}
              onChange={() => handleServiceToggle(params.row.id)}
              sx={{ color: "#333", "&.Mui-checked": { color: "#333" } }}
            />
            <ByzMuiOpenProcessMenu
              menuItems={
                currentView === "frequent"
                  ? [
                    {
                      label: t("patient-request-service.favorites.remove"),
                      iconColor: "error",
                      onClick: async () =>
                        await handleRemoveFromFavorites(params.row.id, params.row.name),
                    },
                  ]
                  : [
                    {
                      label: t("patient-request-service.favorites.add"),
                      iconColor: "success",
                      onClick: async () =>
                        await handleAddToFavorites(params.row.id, params.row.name),
                    },
                  ]
              }
              onClose={() => { }}
              sx={{ marginRight: "8px" }}
            />
            <Box sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "1px", backgroundColor: "#666" }} />
          </Box>
        ),
      },
      {
        field: "name",
        headerName: t("patient-request-service.grid.columns.service-name"),
        flex: 1,
        minWidth: 200,
      },
      {
        field: "st_name",
        headerName: t("patient-request-service.grid.columns.service-type"),
        width: 150,
      },
      {
        field: "code",
        headerName: t("patient-request-service.grid.columns.official-code"),
        width: 120,
      },
      {
        field: "note",
        headerName: t("patient-request-service.grid.columns.note"),
        width: 150,
      },
      {
        field: "ot_name",
        headerName: t("patient-request-service.grid.columns.operation-type"),
        width: 150,
      },
      {
        field: "gpc_name",
        headerName: t("patient-request-service.grid.columns.medula-code"),
        width: 140,
      },
    ];

    if (huv) {
      const insertIndex = base.findIndex((c) => c.field === "code") + 1;
      const huvColumns: GridColDef[] = [
        { field: "huv_name", headerName: t("patient-request-service.grid.columns.huv-name"), width: 160 },
        { field: "huv_code", headerName: t("patient-request-service.grid.columns.huv-code"), width: 140 },
        { field: "huv_coef", headerName: t("patient-request-service.grid.columns.huv-coef"), width: 140 },
      ];
      base.splice(insertIndex, 0, ...huvColumns);
    }

    return base;
  }, [currentView, handleAddToFavorites, handleRemoveFromFavorites, handleServiceToggle, huv, selectedServices, t]);



  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader={true}
      showPatientData={false}
    >
      <Box sx={{
        height: "auto",
        minHeight: "90vh",
        display: "flex",
        transition: "height 0.3s ease-in-out",
        position: "relative",
        pb: "60px",
        overflow: "auto"
      }}>

        <SidebarArea
          title={title}
          description={description}
          upn={upn}
          protocolId={protocolId}
          sidebarSearchText={sidebarSearchText}
          setSidebarSearchText={setSidebarSearchText}
          mainCategories={mainCategories}
          selectedMainCategory={selectedMainCategory}
          handleMainCategorySelect={handleMainCategorySelect}
          visibleCategories={visibleCategories}
          expandedCategories={expandedCategories}
          selectedSubCategory={selectedSubCategory}
          handleSubCategorySelect={handleSubCategorySelect}
          handleCategoryToggle={handleCategoryToggle}
        />


        <Box sx={{ flex: 1, p: { xs: 1, sm: 2 }, position: "relative", minWidth: 0 }}>

          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 1
          }}>
            <ByzMuiTypography isTitle={true} sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
              {t("patient-request-service.new-service")}
            </ByzMuiTypography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <ByzMuiCloseButton onClick={onClose} />
            </Box>
          </Box>

          <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: "center" }}>
            <ByzMuiTextField
              size="small"
              placeholder={t("patient-request-service.search.filter")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small">
                      <FilterListIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 500, md: 660 },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f4f5f7",
                  "& fieldset": {
                    border: "none"
                  },
                  "&:hover fieldset": {
                    border: "none"
                  },
                  "&.Mui-focused fieldset": {
                    border: "none"
                  }
                }
              }}
              value={contentSearchText}
              onChange={(e) => setContentSearchText(e.target.value)}
            />
            <Box sx={{
              width: "30px",
              height: "30px",
              padding: "1px",
              scale: "0.8",
              mb: "20px"
            }}>
              <ByzMuiSearchButton />

            </Box>
            <FormControlLabel
              control={<Checkbox checked={huv} onChange={(e) => setHuv(e.target.checked)} />}
              label={t("patient-request-service.form.huv")}
              sx={{ mt: 1, mx: 2 }}
            />
            <ByzMuiSelect
              label={t("patient-request-service.form.price-list-period")}
              options={priceListPeriods.map(plp => ({ label: plp.name, value: plp.id }))}
              value={priceListPeriods.find(plp => plp.id === selectedPlpId) ? { label: priceListPeriods.find(plp => plp.id === selectedPlpId).name, value: selectedPlpId } : null}
              onChange={(e: any, newValue: any) => {
                setSelectedPlpId(newValue ? newValue.value : null);
              }}
              sx={{ width: 150, mt: "2px" }}
              placeholder={t("patient-request-service.form.price-list-period")}
            />

          </Stack>


          <ServiceGridArea
            currentView={currentView}
            rowID={rowID}
            onHistoryServiceToggle={handleServiceToggle}
            selectedHistoryServices={selectedHistoryServices}
            setSelectedHistoryServices={setSelectedHistoryServices}
            onHistoryParentToggle={handleHistoryParentToggle}
            searchText={contentSearchText}
            gridData={gridData}
            columns={columns}
            loading={loadingServices || isServiceProcessing}
          />


        </Box>
      </Box>

      <SelectedServicesPanel
        data={selectedServicesData}
        selectedItems={selectedServices}
        config={{
          title: t("patient-request-service.selected-services.title"),
          showTestDurations: false,
          showPatientDebt: false,
          showAddToPatient: true,
          showVisibilityButton: false,
          showFullScreenButton: true,
          showEditButton: true,
        }}
        enableRowDetails={true}
        detailAccessor={"breakdown"}
        detailPanelHeight={180}
        onEdit={(row: any) => {
          setEditRow(row);
          setEditModalOpen(true);
        }}
        columns={[
          {
            field: "name",
            headerName: t("patient-request-service.selected-services.columns.service-name"),
            flex: 1,
            minWidth: 220,
          },
          {
            field: "change",
            headerName: t("patient-request-service.selected-services.columns.change"),
            width: 110,
            sortable: false,
            filterable: false,
            renderCell: () => (
              <Box sx={{ color: "primary.main", fontWeight: 600 }}>…</Box>
            ),
          },
          {
            field: "direction",
            headerName: t("patient-request-service.selected-services.columns.direction"),
            width: 120,
          },
          {
            field: "session",
            headerName: t("patient-request-service.selected-services.columns.session"),
            width: 120,
          },
          {
            field: "quantity",
            headerName: t("patient-request-service.selected-services.columns.quantity"),
            width: 90,
            type: "number",
            editable: true,
          },
          {
            field: "patientTotal",
            headerName: t("patient-request-service.selected-services.columns.patient-total"),
            width: 130,
          },
          {
            field: "institutionTotal",
            headerName: t("patient-request-service.selected-services.columns.institution-total"),
            width: 130,
          },
          {
            field: "total",
            headerName: t("patient-request-service.selected-services.columns.total"),
            width: 120,
          },
          {
            field: "doctor",
            headerName: t("patient-request-service.selected-services.columns.doctor"),
            width: 140,
          },
          {
            field: "doctorRole",
            headerName: t("patient-request-service.selected-services.columns.doctor-role"),
            width: 120,
          },
          {
            field: "outOfPackage",
            headerName: t("patient-request-service.selected-services.columns.out-of-package"),
            width: 120,
          },
        ]}
        onItemToggle={handleServiceToggle}
        onSelectAll={() => { }}
        onTestDurationsClick={() => { }}
        onPatientDebtClick={() => { }}
        onAddToPatientClick={handleAddToPatientClick}
        addToPatientDisabled={selectedServicesData.length === 0}
        isLoading={isSubmittingToPatient}
        onVisibilityClick={() => { }}
      />

      <EditOperationModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        operationData={{
          id: editRow?.id || 0,
          name: editRow?.name || "",
          quantity: editRow?.quantity || 1,
          direction: editRow?.direction || "",
          session: editRow?.session || "",
          doctor: editRow?.doctor || "",
          doctorRole: editRow?.doctorRole || "",
          changeOption: editRow?.changeOption || "",
          generalSession: editRow?.generalSession || "",
          generalSessionId: editRow?.generalSessionId || "",
          serviceType: editRow?.st_name || editRow?.ot_name || "",
        }}
        patientId={rowID}
        onSave={async (updated) => {
          // Find the service before update to check if quantity changed
          const previousService = selectedServicesData.find((it) => it.id === updated.id);
          const quantityChanged = previousService && previousService.quantity !== updated.quantity;

          // Update the service data
          setSelectedServicesData((prev) => prev.map((it) => it.id === updated.id ? {
            ...it,
            quantity: updated.quantity,
            direction: updated.direction,
            session: updated.generalSession || updated.session, // Seans alanını generalSession ile güncelle
            doctor: updated.doctor,
            doctorRole: updated.doctorRole,
            changeOption: updated.changeOption,
            generalSession: updated.generalSession,
            generalSessionId: updated.generalSessionId
          } : it));

          // If quantity changed, fetch new price
          if (quantityChanged && updated.id) {
            await fetchPriceForService(Number(updated.id), updated.quantity);
          }
        }}
        onDoctorInfoSave={(data) => {
          if (editRow?.id) {
            upsertAddInfoForPendingService("ASK_DOCTOR_REQUEST", data, editRow.id);
          }
        }}
      />

      <WillOpenCardModals
        rowID={rowID}
        title={title}
        openHeightInfoModal={openHeightInfoModal}
        openVkiModal={openVkiModal}
        openEgfrModal={openEgfrModal}
        openProcessDirectionModal={openProcessDirectionModal}
        openDirectionModal={openDirectionModal}
        openAskDoctorModal={openAskDoctorModal}
        openPregnantControlModal={openPregnantControlModal}
        openPregnantModal={openPregnantModal}
        openHivModal={openHivModal}
        openPacemakerModal={openPacemakerModal}
        openLabModal={openLabModal}
        openInvestigationModal={openInvestigationModal}
        openContrastModal={openContrastModal}
        openContrastRequestModal={openContrastRequestModal}
        openConsultationModal={openConsultationModal}
        openPathologyModal={openPathologyModal}
        setOpenVkiModal={setOpenVkiModal}
        setOpenEgfrModal={setOpenEgfrModal}
        setOpenDirectionModal={setOpenDirectionModal}
        setOpenAskDoctorModal={setOpenAskDoctorModal}
        setOpenPregnantModal={setOpenPregnantModal}
        setOpenHivModal={setOpenHivModal}
        setOpenPacemakerModal={setOpenPacemakerModal}
        setOpenLabModal={setOpenLabModal}
        setOpenInvestigationModal={setOpenInvestigationModal}
        setOpenContrastModal={setOpenContrastModal}
        setOpenContrastRequestModal={setOpenContrastRequestModal}
        setOpenConsultationModal={setOpenConsultationModal}
        setOpenPathologyModal={setOpenPathologyModal}
        setPendingAddToPatient={setPendingAddToPatient}
        activeWillOpenCardIndex={activeWillOpenCardIndex}
        activeWillOpenCardServiceName={activeWillOpenCardServiceName}
        pendingWillOpenCards={pendingWillOpenCards}
        handleWillOpenCardModalCancelled={handleWillOpenCardModalCancelled}
        handleWillOpenCardModalClosed={handleWillOpenCardModalClosed}
        upsertAddInfoForPendingService={upsertAddInfoForPendingService}
        handleVkiModalSave={handleVkiModalSave}
        handleEgfrModalSave={handleEgfrModalSave}
        handleDirectionSave={handleDirectionSave}
        handleAskDoctorSave={handleAskDoctorSave}
        handlePregnantSave={handlePregnantSave}
        handleHivSave={handleHivSave}
        handlePacemakerSave={handlePacemakerSave}
        handleLabSave={handleLabSave}
        handleInvestigationSave={handleInvestigationSave}
      />
    </OpenComponentDialog>
  );
};

export default RequestServiceModal;
